<template>
  <div v-if="tableLoading">
    <v-container style="height: 200px;">
      <v-row
        class="fill-height"
        align="center" 
        justify="center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </v-row>     
    </v-container>
  </div>
  <div 
    v-else
    class="mx-3"
  >
    <v-row>
      <v-col
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <setup-claims-approval-workflow-claim-assignee-dialog-create
          v-if="$hasPermissionTo(['create claim assignees']) && showButton()"
          :companies="companies"
          :departments="departments"
          :employees="employees"
          :claim-approval-workflow-batch="claimApprovalWorkflowBatch"
          @created="loadData"
        />
      </v-col>
      <v-spacer />

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-select
          v-model="statusFilter"
          :items="items"
          item-text="state"
          item-value="value"
          label="Claim Assignee's Status"
          placeholder="Active / Deleted"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-text-field
          v-model="search"
          placeholder="Key in name here"
          append-icon="search"
          label="Search Claim Assignee"
          hide-no-data
          hide-selected
          clearable 
        />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredClaimAssignees"
      :single-expand="true"
      show-expand
      :loading="tableLoading"
      :search="search"
    >
      <template v-slot:item="props">
        <tr>
          <td :class="props.isExpanded?'remove-bottom-border':'border-bottom'">
            <v-btn
              icon
              @click="props.expand(!props.isExpanded)"
            >
              <v-icon small> 
                {{ props.isExpanded ? 'fa-chevron-up':'fa-chevron-down' }} 
              </v-icon>
            </v-btn>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border text-center':'border-bottom text-center' ">
            <span class="font-weight-bold">
              {{ props.item.approval_level }}
            </span>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
            <span :class="props.item.employee.end_date && ($moment(props.item.employee.end_date) <= $moment()) ? 'red--text font-weight-bold' : 'font-weight-bold'">
              {{ props.item.employee.person.name }}
            </span>
            <v-tooltip
              v-if="props.item.employee.end_date"
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  color="warning"
                  v-on="on"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <div>{{ 'Last Working Date: ' + props.item.employee.last_working_date }}</div>
              <div>{{ 'Resigned On: ' + props.item.employee.end_date }}</div>
            </v-tooltip>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
            <span class="font-weight-bold">
              {{ props.item.employee.department.name }}
            </span>
          </td>

          <td :class="props.enable_threshold ? 'remove-bottom-border text-center' : 'border-bottom text-center'">
            <div class="text-center">
              <template v-if="props.item.enable_threshold">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span
                      v-on="on"
                    >
                      <v-icon color="hrissecondary">mdi-check-outline</v-icon>
                    </span>
                  </template>
                  <div 
                    v-for="rule in props.item.claim_threshold_rules"
                    :key="rule.id"
                    class="text-left"
                  >
                    <span v-if="rule.type === 'Amount'">{{ rule.type + ' > RM ' + rule.threshold_value }}</span>
                    <span v-else>{{ rule.type + ' > ' + rule.threshold_value + ' days' }}</span>
                  </div>
                </v-tooltip>
              </template>

              <template v-else>
                <v-icon color="error">
                  mdi-close-outline
                </v-icon>
              </template>
            </div>
          </td>

          <td :class="props.is_final_approver? 'remove-bottom-border text-center':'border-bottom text-center' ">
            <div>
              <span>
                <v-icon
                  v-if="props.item.is_final_approver"
                  color="hrissecondary"
                >
                  mdi-check-outline
                </v-icon>
                <v-icon
                  v-else
                  color="error"
                >
                  mdi-close-outline
                </v-icon>
              </span>
            </div>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
            <span v-if="statusFilter">
              <span class="mx-1" />
              <setup-claims-approval-workflow-claim-assignee-dialog-edit
                v-if="$hasPermissionTo(['update claim assignees']) && showButton()"
                :claim-assignee="props.item"
                :companies="companies"
                :departments="departments"
                :employees="employees"
                :claim-approval-workflow-batch="claimApprovalWorkflowBatch"
                @updated="loadData"
              />
              <setup-claims-approval-workflow-claim-assignee-dialog-delete
                v-if="$hasPermissionTo(['delete claim assignees']) && showButton()"
                :claim-assignee="props.item"
                @deleted="loadData"
              />
            </span>
            <span v-else>
              <setup-claims-approval-workflow-claim-assignee-dialog-restore
                v-if="$hasPermissionTo(['restore claim assignees']) && showButton()"
                :claim-assignee="props.item"
                @restored="loadData"
              />
            </span>
          </td>
        </tr>
      </template>
            
      <template v-slot:expanded-item="{ headers, item }">   
        <td
          class="details pb-3"
          :colspan="12"
        >
          <v-data-table
            hide-default-footer
            style="border:1px solid #e6e6e6"
            dense
            class="mt-2"
            :items="getSortedAssignees(item.claim_assignee_companies)"
            :headers="assigneeCompanyHeaders"
            :loading="tableLoading"
            :items-per-page="-1"
          >
            <template v-slot:item.company_name="{ item }">
              <span>{{ (item.company || {}).name }}</span>
            </template>

            <template v-slot:item.end_date="{ item }">
              <span>{{ item.end_date ? item.end_date : 'N/A' }}</span>
            </template>
            
            <template v-slot:item.action="{ item }">
              <setup-claims-approval-workflow-claim-assignee-company-dialog-edit
                v-if="$hasPermissionTo(['update claim assignees']) && showButton()"
                :claim-assignee-company="item"
                :claim-approval-workflow-batch="claimApprovalWorkflowBatch"
                @updated="loadData"
              />
              <setup-claims-approval-workflow-claim-assignee-company-dialog-delete
                v-if="$hasPermissionTo(['delete claim assignees']) && showButton()"
                :claim-assignee-company="item"
                @deleted="loadData"
              />
            </template>
          </v-data-table>
        </td>
      </template>
    </v-data-table>

    <span class="subtitle-2 font-weight-medium font-italic">
      <div>
        <span class="font-weight-bold red--text">Name in Red</span> = Resigned Employee
      </div>
    </span>
  </div>
</template>

<script>
  export default {
    components: {
      SetupClaimsApprovalWorkflowClaimAssigneeDialogCreate: () => import('@/components/hris/setup/claims/approval-workflow/claim-assignee/DialogCreate'),
      SetupClaimsApprovalWorkflowClaimAssigneeDialogEdit: () => import('@/components/hris/setup/claims/approval-workflow/claim-assignee/DialogEdit'),
      SetupClaimsApprovalWorkflowClaimAssigneeDialogDelete: () => import('@/components/hris/setup/claims/approval-workflow/claim-assignee/DialogDelete'),
      SetupClaimsApprovalWorkflowClaimAssigneeDialogRestore: () => import('@/components/hris/setup/claims/approval-workflow/claim-assignee/DialogRestore'),
      SetupClaimsApprovalWorkflowClaimAssigneeCompanyDialogEdit: () => import('@/components/hris/setup/claims/approval-workflow/claim-assignee/company/DialogEdit'),
      SetupClaimsApprovalWorkflowClaimAssigneeCompanyDialogDelete: () => import('@/components/hris/setup/claims/approval-workflow/claim-assignee/company/DialogDelete'),
    },
    props: {
      claimApprovalWorkflowBatch: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        numberitems: [
          { id: 1, text: 'Superior' },
          { id: 2, text: 'HR' },
          { id: 3, text: 'Finance' },
        ],
        loading: false,
        claimAssignees: [],
        trashedClaimAssignees: [],
        headers: [
          {
            text: 'Approval Level',
            align: 'center',
            value: 'approval_level',
            width: '180px',
          },
          {
            text: 'Name',
            align: 'left',
            value: 'employee.person.name',
          },
          {
            text: 'Department',
            align: 'left',
            value: 'employee.department.name',
          },
          {
            text: 'Enabled Threshold?',
            align: 'center',
            value: 'enable_threshold',
            sortable: false,
          },
          {
            text: 'Is Final Approver?',
            align: 'center',
            value: 'is_final_approver',
            sortable: false,
          },
          {
            text: '',
            sortable: false,
            value: 'action',
          },
        ],
        assigneeCompanyHeaders: [
          {
            text: 'Company',
            align: 'left',
            value: 'company_name',
          },
          {
            text: 'Start Date',
            align: 'center',
            value: 'start_date',
          },
          {
            text: 'End Date',
            align: 'center',
            value: 'end_date',
          },
          {
            text: '',
            sortable: false,
            value: 'action',
          },
        ],
        search: '',
        statusFilter: { state: 'Active', value: true },
        items: [
          { state: 'Active', value: true },
          { state: 'Deleted', value: false },
        ],
        tableLoading: true,
        companies: [],
        employees: [],
        departments: [],
        appOrg: process.env.VUE_APP_ORG,
      }
    },
    computed: {
      filteredClaimAssignees () {
        return this.statusFilter ? this.claimAssignees : this.trashedClaimAssignees
      },
    },
    watch: {
      claimApprovalWorkflowBatch: {
        immediate: true,
        handler (value) {
          if (value) {
            this.loadData()
          }
        },
      },
    },
    created () {
      this.loadData()

      if (this.$hasPermissionTo(['create claim assignees']) || this.$hasPermissionTo(['update claim assignees'])) {
        Promise.all([
          this.$api.hrisEmployees().list('?with=active'),
          this.$api.hrisDepartments().list(['name'], false, true),
          this.$api.hrisCompanies().list('?organization=' + this.appOrg),
        ])
          .then(([employees, departments, companies]) => {
            let temp = employees.data.filter(employee => {
              return employee.department_id !== null && employee.status === 'complete'
            })

            this.employees = _.orderBy(temp, ['person.name'], ['asc'])
            this.departments = departments.data
            this.companies = _.orderBy(companies.data, ['name'], ['asc'])
            this.companies.unshift({ id: 'ALL', name: 'All Companies' })
          })
      }
    },
    methods: {
      showButton () {
        if (this.claimApprovalWorkflowBatch && this.$moment(this.claimApprovalWorkflowBatch.end_date).isBefore(this.$moment(), 'day')) {
          return false
        }

        return true
      },
      loadData () {
        this.tableLoading = true

        Promise.all([
          this.$api.claimAssignees().list('?batch=' + this.claimApprovalWorkflowBatch.id),
          this.$api.claimAssignees().listTrashed('?batch=' + this.claimApprovalWorkflowBatch.id),
        ]).then(([claimAssignees, trashedClaimAssignees]) => {
          this.claimAssignees = _.orderBy(claimAssignees.data, ['approval_level', 'employee.person.name'], ['asc', 'desc'])
          this.trashedClaimAssignees = _.orderBy(trashedClaimAssignees.data, ['approval_level', 'employee.person.name'], ['asc', 'desc'])

          this.tableLoading = false
        })
      },
      getSortedAssignees (companies) {
        return companies.slice().sort((a, b) =>
          a.company.name.localeCompare(b.company.name)
        )
      },
    },
  }
</script>
